import React, { useMemo } from 'react';

import { Helmet } from 'react-helmet';

import {
  Alert,
  Box,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';

import { useListAudienceUsers } from '~/hooks/apis/promo/useListAudienceUsers';
import { useListAudiences } from '~/hooks/apis/promo/useListAudiences';

type Row = Record<string, unknown>;

const firstPresent = (row: Row, keys: Array<string>) => {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '') {
      return String(value);
    }
  }
  return '—';
};

const rowLabel = (row: Row, fallbackKey: string) =>
  firstPresent(row, [fallbackKey, 'name', 'title', 'label', 'audience_name', 'audienceId', 'audience_id', 'id']);

function ListTable({
  title,
  description,
  rows,
  isLoading,
  isError,
  errorMessage,
  emptyMessage,
  columns,
}: {
  title: string;
  description: string;
  rows: Array<Row>;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  emptyMessage: string;
  columns: Array<{ key: string; label: string }>;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Stack>

      {isLoading ? <Spinner /> : null}
      {isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      ) : null}
      {!isLoading && !isError && rows.length === 0 ? <Alert severity="info">{emptyMessage}</Alert> : null}
      {!isLoading && !isError && rows.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={180}>Label</TableCell>
                {columns.map((column) => (
                  <TableCell key={column.key}>{column.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={String(row.id ?? row.audience_id ?? row.audienceUserId ?? index)} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{rowLabel(row, 'id')}</TableCell>
                  {columns.map((column) => (
                    <TableCell key={column.key}>{firstPresent(row, [column.key])}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Paper>
  );
}

export default function AudiencesPage() {
  const audienceQuery = useMemo(() => ({ page: 0, limit: 100 }), []);
  const audienceUsersQuery = useMemo(() => ({ page: 0, limit: 100 }), []);

  const audiences = useListAudiences({ query: audienceQuery });
  const audienceUsers = useListAudienceUsers({ query: audienceUsersQuery });

  const audienceRows = audiences.data?.result?.audiences ?? [];
  const audienceUserRows = audienceUsers.data?.result?.audienceUsers ?? [];

  const audienceColumns = useMemo(
    () => [
      { key: 'id', label: 'ID' },
      { key: 'description', label: 'Description' },
      { key: 'created_at', label: 'Created at' },
      { key: 'updated_at', label: 'Updated at' },
    ],
    [],
  );

  const audienceUserColumns = useMemo(
    () => [
      { key: 'audience_id', label: 'Audience ID' },
      { key: 'user_id', label: 'User ID' },
      { key: 'email', label: 'Email' },
      { key: 'created_at', label: 'Created at' },
    ],
    [],
  );

  return (
    <>
      <Helmet>
        <title>Marketing / Audiences</title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader title="Audiences" />
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Read-only for now. Backend pagination can be wired when svc-promo endpoints are finalized.
          </Typography>
        </Box>
        <Stack spacing={3}>
          <ListTable
            title="Audiences"
            description="All audiences exposed by svc-promo."
            rows={audienceRows}
            isLoading={audiences.isLoading}
            isError={audiences.isError}
            errorMessage={audiences.error instanceof Error ? audiences.error.message : 'Failed to load audiences'}
            emptyMessage="No audiences found."
            columns={audienceColumns}
          />
          <ListTable
            title="Audience Users"
            description="Users assigned to an audience."
            rows={audienceUserRows}
            isLoading={audienceUsers.isLoading}
            isError={audienceUsers.isError}
            errorMessage={
              audienceUsers.error instanceof Error ? audienceUsers.error.message : 'Failed to load audience users'
            }
            emptyMessage="No audience users found."
            columns={audienceUserColumns}
          />
        </Stack>
      </Container>
    </>
  );
}
